import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowLeft, CheckCircle2, FileText, Images, Loader2, Paperclip, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import AccessRestricted from '../../../components/AccessRestricted';
import { ImageLightbox } from '../../../components/ui/image-lightbox';
import type { LightboxImage } from '../../../components/ui/image-lightbox';
import { cn } from '../../../components/ui/utils';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import {
    ProcurementMeta,
    PurchaseOrderDetail,
    PurchaseOrderLine,
    PurchaseOrderReceipt,
    ReceiptLineCondition,
} from '../../../types/procurement.types';
import { ApiError } from '../../../utils/api';
import { procurementMetaApi, purchaseOrdersApi } from '../../../utils/procurement-api';
import { compressImage } from '../../../utils/upload';
import { usePageTitle } from '../../../utils/usePageTitle';
import { clampQuantity, formatDate, formatDateTime, orderReference, vendorName } from './procurement-ui';
import { PurchaseOrderStatusPill } from './components/PurchaseOrderStatus';

/** The quantity and condition being entered against one PO line. */
interface LineDraft {
    qty: string;
    condition: ReceiptLineCondition;
}

const CONDITION_LABELS: Record<ReceiptLineCondition, string> = {
    good: 'Good',
    damaged: 'Damaged',
    short: 'Short',
};

const CONDITION_ACTIVE: Record<ReceiptLineCondition, string> = {
    good: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    damaged: 'border-amber-300 bg-amber-50 text-amber-800',
    short: 'border-red-300 bg-red-50 text-red-700',
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * What each open line should show when the page opens: the quantity already claimed on this
 * receipt, or the whole outstanding quantity as the default — most deliveries are complete
 * and the yard only has to correct the exceptions.
 */
const defaultDrafts = (order: PurchaseOrderDetail, receipt: PurchaseOrderReceipt | null) => {
    const drafts: Record<number, LineDraft> = {};
    order.lines.forEach((line) => {
        const claimed = receipt?.lines.find((entry) => entry.purchaseOrderLineId === line.id) ?? null;
        if (!claimed && line.outstanding <= 0) return;
        drafts[line.id] = {
            qty: String(claimed ? claimed.quantityReceived : line.outstanding),
            condition: claimed?.condition ?? 'good',
        };
    });
    return drafts;
};

const serverMessage = (err: unknown, fallback: string) => {
    if (err instanceof ApiError) {
        // Error bodies are `{ message }` — the post route reports exactly what is missing.
        const body = err.data as { message?: string } | null;
        return body?.message || err.message || fallback;
    }
    return err instanceof Error ? err.message || fallback : fallback;
};

/**
 * Log a receipt — the mobile-first goods-in flow (STOCK.md §6.2, applied to purchase orders).
 *
 * One page, four steps: the docket, the quantities, the photos, the post. The receipt record
 * is created at step one and every later step hangs off it, so a phone that dies mid-delivery
 * can be picked up again — the page resumes the in-progress receipt instead of starting a
 * second one.
 *
 * Photos are picked from the device in a batch — a delivery often lands as a set of shots
 * already on the phone. Each file is compressed and posted individually, because the photo
 * endpoint takes one file per request.
 * The minimum count and its wording come from procurement settings, and the server enforces
 * the minimum again at post time — the client-side gate is a courtesy, not the rule.
 */
export default function ReceiptCapturePage() {
    usePageTitle();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.PURCHASE_ORDER);

    const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
    const [meta, setMeta] = useState<ProcurementMeta | null>(null);
    const [receipt, setReceipt] = useState<PurchaseOrderReceipt | null>(null);
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    const [docket, setDocket] = useState('');
    const [receiptDate, setReceiptDate] = useState(today());
    const [carrier, setCarrier] = useState('');
    const [notes, setNotes] = useState('');
    const [drafts, setDrafts] = useState<Record<number, LineDraft>>({});
    const [photoIndex, setPhotoIndex] = useState<number | null>(null);

    const documentInput = useRef<HTMLInputElement | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const [detail, settings] = await Promise.all([
                purchaseOrdersApi.get(id),
                procurementMetaApi.get(),
            ]);
            setOrder(detail);
            setMeta(settings);

            // An in-progress receipt is resumed, never duplicated: the docket has already
            // been recorded against this PO.
            const existing = detail.receipts.find((entry) => entry.status === 'in_progress') ?? null;
            setReceipt(existing);
            setDrafts(defaultDrafts(detail, existing));
            if (existing) {
                setDocket(existing.docketNumber);
                setReceiptDate(existing.receiptDate.slice(0, 10));
                setCarrier(existing.carrier ?? '');
                setNotes(existing.notes ?? '');
            }
            setAccessDenied(null);
        } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            if (err instanceof ApiError && err.status === 404) {
                setNotFound(true);
                return;
            }
            toast.error(serverMessage(err, 'Failed to load the purchase order'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return (
            <AccessRestricted
                accessMessage={accessDenied}
                backMessage="Back to Receiving"
                navigateTo="/stock/procurement/receiving"
            />
        );
    }

    if (notFound) {
        return (
            <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
                <FileText size={48} className="text-muted-foreground" />
                <h2 className="text-xl font-semibold">Purchase order not found</h2>
                <Button variant="outline" onClick={() => navigate('/stock/procurement/receiving')}>
                    Back to Receiving
                </Button>
            </div>
        );
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    const reference = orderReference(order);
    const minPhotos = meta?.minReceiptPhotos ?? 0;
    const photoHelperText = meta?.photoHelperText ?? '';
    const photos = receipt?.photos ?? [];
    const savedLines = receipt?.lines.length ?? 0;
    const hasDocket = Boolean(receipt && receipt.docketNumber.trim());
    const photoOk = photos.length >= minPhotos;
    const outstandingLines = order.lines.filter(
        (line) => line.outstanding > 0 || Boolean(receipt?.lines.some((entry) => entry.purchaseOrderLineId === line.id)));
    /** Rows the user has left (or typed) above zero but that have not been saved yet. */
    const pendingLines = outstandingLines.filter(
        (line) => clampQuantity(Number(drafts[line.id]?.qty ?? 0), line.outstanding) > 0).length;
    const hasLines = savedLines > 0 || pendingLines > 0;
    const canPost = Boolean(receipt) && hasLines && photoOk && hasDocket;

    const blockedText = !hasDocket
        ? 'Docket slip # is required on every receipt — it anchors the PO ↔ docket ↔ invoice match.'
        : !hasLines
            ? 'Add at least one line before posting — the receipt has to say what arrived.'
            : `Add ${minPhotos - photos.length} more photo(s) to submit. ${photoHelperText}`.trim();

    const lightboxImages: LightboxImage[] = photos.map((photo) => ({
        id: photo.id,
        url: photo.url,
        label: `${receipt?.docketNumber ?? reference} · ${formatDateTime(photo.serverTimestamp)}`,
        // The read routes return the photo's raw row (`userName`) while the contract declares
        // `capturedBy` — don't assume the nested object is there.
        caption: photo.capturedBy?.name ?? 'Not recorded',
    }));

    const refreshReceipt = async () => {
        if (!id || !receipt) return;
        setReceipt(await purchaseOrdersApi.getReceipt(id, receipt.id));
    };

    /** Save the docket. This is what creates the receipt record; everything else needs it. */
    const saveHeader = async () => {
        if (!docket.trim()) return;
        try {
            setBusy('header');
            const body = {
                receiptDate,
                docketNumber: docket.trim(),
                carrier: carrier.trim() || undefined,
                notes: notes.trim() || undefined,
            };
            if (receipt) {
                setReceipt(await purchaseOrdersApi.updateReceipt(order.id, receipt.id, body));
                toast.success('Receipt details saved');
            } else {
                setReceipt(await purchaseOrdersApi.addReceipt(order.id, body));
                toast.success('Docket saved — now the quantities');
            }
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to save the receipt details'));
        } finally {
            setBusy(null);
        }
    };

    /**
     * Persist one line. Quantities are clamped to what is still outstanding, and a line
     * brought back to zero is removed rather than stored as a receipt of nothing.
     */
    const commitLine = async (line: PurchaseOrderLine, patch: Partial<LineDraft> = {}) => {
        if (!receipt) return;
        const fallback: LineDraft = { qty: String(line.outstanding), condition: 'good' };
        const draft: LineDraft = { ...fallback, ...(drafts[line.id] ?? {}), ...patch };
        const quantity = clampQuantity(Number(draft.qty), line.outstanding);
        setDrafts((prev) => ({ ...prev, [line.id]: { ...draft, qty: String(quantity) } }));

        const existing = receipt.lines.find((entry) => entry.purchaseOrderLineId === line.id) ?? null;
        if (quantity === (existing?.quantityReceived ?? 0) && draft.condition === (existing?.condition ?? 'good')) {
            return;
        }

        try {
            setBusy(`line:${line.id}`);
            if (quantity <= 0) {
                if (existing) await purchaseOrdersApi.deleteReceiptLine(order.id, receipt.id, existing.id);
            } else if (existing) {
                await purchaseOrdersApi.updateReceiptLine(order.id, receipt.id, existing.id, {
                    quantityReceived: quantity,
                    condition: draft.condition,
                });
            } else {
                await purchaseOrdersApi.addReceiptLine(order.id, receipt.id, {
                    purchaseOrderLineId: line.id,
                    quantityReceived: quantity,
                    condition: draft.condition,
                });
            }
            await refreshReceipt();
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to save the line'));
        } finally {
            setBusy(null);
        }
    };

    /**
     * Upload a batch of photos. The endpoint accepts a single file per request, so files go
     * up one at a time and the receipt is refreshed once; a file that fails does not abandon
     * the rest of the batch.
     */
    const capturePhotos = async (files: File[]) => {
        if (!receipt || files.length === 0) return;
        try {
            setBusy('photo');
            let failed = 0;
            for (const file of files) {
                try {
                    const compressed = await compressImage(file);
                    await purchaseOrdersApi.uploadReceiptPhoto(order.id, receipt.id, compressed);
                } catch {
                    failed += 1;
                }
            }
            await refreshReceipt();
            const added = files.length - failed;
            if (added > 0) toast.success(added === 1 ? 'Photo added' : `${added} photos added`);
            if (failed > 0) {
                toast.error(failed === 1
                    ? 'One photo failed to upload'
                    : `${failed} photos failed to upload`);
            }
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to upload the photos'));
        } finally {
            setBusy(null);
        }
    };

    /** Reset the input first so the same files can be picked again. */
    const onPhotoPicked = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length) capturePhotos(files);
    };

    const attachDocument = async (file: File) => {
        if (!receipt) return;
        try {
            setBusy('document');
            await purchaseOrdersApi.uploadReceiptDocument(order.id, receipt.id, file);
            await refreshReceipt();
            toast.success('Document attached');
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to upload the document'));
        } finally {
            setBusy(null);
        }
    };

    const removePhoto = async (photoId: number) => {
        if (!receipt) return;
        try {
            setBusy(`removePhoto:${photoId}`);
            await purchaseOrdersApi.deleteReceiptPhoto(order.id, receipt.id, photoId);
            await refreshReceipt();
            toast.success('Photo removed');
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to remove the photo'));
        } finally {
            setBusy(null);
        }
    };

    const removeDocument = async (documentId: number) => {
        if (!receipt) return;
        try {
            setBusy(`removeDocument:${documentId}`);
            await purchaseOrdersApi.deleteReceiptDocument(order.id, receipt.id, documentId);
            await refreshReceipt();
            toast.success('Document removed');
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to remove the document'));
        } finally {
            setBusy(null);
        }
    };

    /**
     * Push every row that differs from what is stored. A quantity typed but never blurred
     * still belongs to the receipt, so posting flushes them first.
     */
    const flushDrafts = async (activeReceipt: PurchaseOrderReceipt) => {
        for (const line of outstandingLines) {
            const draft = drafts[line.id];
            if (!draft) continue;
            const quantity = clampQuantity(Number(draft.qty), line.outstanding);
            const existing = activeReceipt.lines.find((entry) => entry.purchaseOrderLineId === line.id) ?? null;
            if (quantity === (existing?.quantityReceived ?? 0)
                && draft.condition === (existing?.condition ?? 'good')) continue;

            if (quantity <= 0) {
                if (existing) await purchaseOrdersApi.deleteReceiptLine(order.id, activeReceipt.id, existing.id);
            } else if (existing) {
                await purchaseOrdersApi.updateReceiptLine(order.id, activeReceipt.id, existing.id, {
                    quantityReceived: quantity,
                    condition: draft.condition,
                });
            } else {
                await purchaseOrdersApi.addReceiptLine(order.id, activeReceipt.id, {
                    purchaseOrderLineId: line.id,
                    quantityReceived: quantity,
                    condition: draft.condition,
                });
            }
        }
    };

    /** Post applies the quantities, flips the receipt to posted and may finish the PO. */
    const post = async () => {
        if (!receipt || !canPost) return;
        try {
            setBusy('post');
            await flushDrafts(receipt);
            const updated = await purchaseOrdersApi.postReceipt(order.id, receipt.id);
            const complete = updated.lines.every((line) => line.quantityReceived >= line.quantityOrdered);
            toast.success(complete
                ? 'Receipt logged — PO fully received, now Ready to Enter'
                : 'Receipt logged — lines still outstanding');
            setBusy(null);
            navigate('/stock/procurement/receiving');
        } catch (err) {
            // The server re-checks the docket, the lines and the photo minimum — surface why.
            toast.error(serverMessage(err, 'Failed to post the receipt'));
            setBusy(null);
        }
    };

    if (!order.abilities.canReceipt) {
        return (
            <AccessRestricted
                accessMessage="You do not have permission to log receipts against purchase orders."
                backMessage="Back to Receiving"
                navigateTo="/stock/procurement/receiving"
            />
        );
    }

    if (order.status !== 'po_issued' && order.status !== 'receiving') {
        return (
            <div className="flex flex-col items-center justify-center h-[50vh] space-y-4 text-center">
                <FileText size={48} className="text-muted-foreground" />
                <h2 className="text-xl font-semibold">Nothing to receive against {reference}</h2>
                <p className="text-muted-foreground max-w-md">
                    Receipts are only logged while a purchase order is issued or partly received.
                    This one is {order.status.replace(/_/g, ' ')}.
                </p>
                <Button variant="outline" onClick={() => navigate(`/stock/procurement/purchase-orders/${order.id}`)}>
                    Open the record
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl pb-28 md:pb-0">
            <div>
                <Button variant="ghost" size="sm" className="mb-2 -ml-2"
                        onClick={() => navigate('/stock/procurement/receiving')}>
                    <ArrowLeft size={16} className="mr-1.5" />Receiving
                </Button>
                <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold">Log a receipt</h1>
                    <PurchaseOrderStatusPill status={order.status} />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                    <span className="font-mono">{reference}</span> · {vendorName(order.vendor)} ·{' '}
                    {order.branch.name} · due {formatDate(order.requiredByDate)}
                </p>
                {receipt && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                        Continuing the receipt docket {receipt.docketNumber} that is already in
                        progress against this PO.
                    </p>
                )}
            </div>

            {/* ------------------------------------------------------ step 1 */}
            <Card>
                <CardContent className="p-4 md:p-6 space-y-4">
                    <div className="flex items-center justify-between gap-2">
                        <div>
                            <h2 className="text-base font-semibold">Step 1 · Docket</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                The docket slip number is required on every receipt — it anchors the
                                PO ↔ docket ↔ invoice match.
                            </p>
                        </div>
                        {receipt && <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="receipt-docket">Docket slip # *</Label>
                            <Input
                                id="receipt-docket"
                                className="h-11"
                                value={docket}
                                placeholder="e.g. CH-88213"
                                onChange={(e) => setDocket(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor="receipt-date">Receipt date *</Label>
                            <Input
                                id="receipt-date"
                                type="date"
                                className="h-11"
                                value={receiptDate}
                                onChange={(e) => setReceiptDate(e.target.value)}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="receipt-carrier">Carrier</Label>
                            <Input
                                id="receipt-carrier"
                                className="h-11"
                                value={carrier}
                                placeholder="Who delivered it — vendor fleet, courier, own vehicle"
                                onChange={(e) => setCarrier(e.target.value)}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="receipt-notes">Notes</Label>
                            <Textarea
                                id="receipt-notes"
                                rows={2}
                                value={notes}
                                placeholder="Anything odd about this delivery."
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <Button
                        className="h-11 w-full sm:w-auto"
                        disabled={!docket.trim() || busy !== null}
                        onClick={saveHeader}
                    >
                        {busy === 'header' && <Loader2 size={16} className="animate-spin mr-2" />}
                        {receipt ? 'Save details' : 'Save docket and continue'}
                    </Button>
                </CardContent>
            </Card>

            {/* ------------------------------------------------------ step 2 */}
            <Card>
                <CardContent className="p-4 md:p-6 space-y-4">
                    <div>
                        <h2 className="text-base font-semibold">Step 2 · Quantities</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            One row per open line. The quantity defaults to what is still
                            outstanding — change it when the delivery is short or damaged.
                        </p>
                    </div>

                    {!receipt ? (
                        <p className="text-sm text-muted-foreground">
                            Save the docket first — the quantities are recorded against it.
                        </p>
                    ) : outstandingLines.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Every line on this PO is already fully received.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {outstandingLines.map((line) => {
                                const draft: LineDraft = drafts[line.id]
                                    ?? { qty: String(line.outstanding), condition: 'good' };
                                return (
                                    <div key={line.id} className="rounded-lg border border-border p-3 space-y-3">
                                        <div>
                                            <p className="text-sm font-medium">{line.description}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {line.outstanding} of {line.quantityOrdered} {line.uom} outstanding
                                                {line.quantityReceived ? ` · ${line.quantityReceived} already received` : ''}
                                            </p>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <div className="sm:w-32">
                                                <Label htmlFor={`receipt-qty-${line.id}`}>Receiving now</Label>
                                                <Input
                                                    id={`receipt-qty-${line.id}`}
                                                    type="number"
                                                    inputMode="decimal"
                                                    min="0"
                                                    max={line.outstanding}
                                                    step="any"
                                                    className="h-11"
                                                    value={draft.qty}
                                                    disabled={busy !== null}
                                                    onChange={(e) => setDrafts((prev) => ({
                                                        ...prev,
                                                        [line.id]: { ...draft, qty: e.target.value },
                                                    }))}
                                                    onBlur={() => commitLine(line)}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <Label>Condition</Label>
                                                <div className="flex gap-2">
                                                    {(['good', 'damaged', 'short'] as ReceiptLineCondition[]).map((condition) => (
                                                        <button
                                                            key={condition}
                                                            type="button"
                                                            disabled={busy !== null}
                                                            onClick={() => commitLine(line, { condition })}
                                                            className={cn(
                                                                'h-11 flex-1 rounded-md border text-xs font-medium transition-colors',
                                                                draft.condition === condition
                                                                    ? CONDITION_ACTIVE[condition]
                                                                    : 'border-border bg-background text-muted-foreground hover:bg-accent',
                                                            )}
                                                        >
                                                            {CONDITION_LABELS[condition]}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ------------------------------------------------------ step 3 */}
            <Card>
                <CardContent className="p-4 md:p-6 space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                            <h2 className="text-base font-semibold">Step 3 · Photos</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Photos are mandatory on every receipt — they are the evidence that
                                the gear on the docket is the gear that arrived.
                            </p>
                        </div>
                        <span className={cn(
                            'inline-flex items-center text-xs font-semibold px-2 py-1 rounded-md border whitespace-nowrap',
                            photoOk
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                : 'border-amber-300 bg-amber-50 text-amber-800',
                        )}>
                            {photos.length} of {minPhotos} required photos
                        </span>
                    </div>

                    {!receipt ? (
                        <p className="text-sm text-muted-foreground">
                            Save the docket first — photos are attached to it.
                        </p>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {photos.map((photo) => (
                                    <div key={photo.id} className="relative aspect-square">
                                        <button
                                            type="button"
                                            className="h-full w-full overflow-hidden rounded-lg border border-border"
                                            onClick={() => setPhotoIndex(photo.id)}
                                            aria-label="Open photo"
                                        >
                                            <img src={photo.url} alt="" className="h-full w-full object-cover" />
                                        </button>
                                        {order.abilities.canDeletePhoto && (
                                            <button
                                                type="button"
                                                aria-label="Remove photo"
                                                onClick={() => removePhoto(photo.id)}
                                                className="absolute top-1 right-1 h-7 w-7 rounded-md bg-black/60 text-white flex items-center justify-center hover:bg-destructive focus:outline-none focus:ring-2 focus:ring-ring"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {/* Gallery pick, several at once: the shots are often already on
                                    the phone when the truck leaves. */}
                                <label className={cn(
                                    'aspect-square rounded-lg border-2 border-dashed border-input flex flex-col',
                                    'items-center justify-center gap-1 px-1 text-center text-xs text-muted-foreground',
                                    'cursor-pointer hover:bg-accent',
                                    !order.abilities.canUploadPhoto && 'opacity-50',
                                    busy !== null && 'pointer-events-none',
                                )}>
                                    {busy === 'photo'
                                        ? <Loader2 size={20} className="animate-spin" />
                                        : <Images size={20} />}
                                    Upload photos
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        disabled={!order.abilities.canUploadPhoto}
                                        onChange={onPhotoPicked}
                                    />
                                </label>
                            </div>

                            <p className="text-xs text-muted-foreground">{photoHelperText}</p>

                            {hasDocket && hasLines && !photoOk && (
                                <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 flex gap-2">
                                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                    <p className="text-xs leading-relaxed">{blockedText}</p>
                                </div>
                            )}

                            <div className="border-t border-border pt-4 space-y-2">
                                <p className="text-xs font-medium">Docket / invoice</p>
                                {receipt.documents.length > 0 && (
                                    <div className="space-y-2">
                                        {receipt.documents.map((document) => (
                                            <div key={document.id}
                                                 className="flex items-center gap-3 rounded-md border border-border p-2">
                                                <FileText size={16} className="text-muted-foreground shrink-0" />
                                                <a href={document.url} target="_blank" rel="noreferrer"
                                                   className="text-sm truncate flex-1 underline">
                                                    {document.filename}
                                                </a>
                                                {order.abilities.canDeleteDocument && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label="Remove document"
                                                        disabled={busy !== null}
                                                        onClick={() => removeDocument(document.id)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {order.abilities.canUploadDocument && (
                                    <>
                                        <input
                                            ref={documentInput}
                                            type="file"
                                            accept="application/pdf,image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                e.target.value = '';
                                                if (file) attachDocument(file);
                                            }}
                                        />
                                        <Button
                                            variant="outline"
                                            className="h-11 w-full sm:w-auto"
                                            disabled={busy !== null}
                                            onClick={() => documentInput.current?.click()}
                                        >
                                            {busy === 'document'
                                                ? <Loader2 size={16} className="animate-spin mr-2" />
                                                : <Paperclip size={16} className="mr-2" />}
                                            {receipt.documents.length
                                                ? 'Attach another document'
                                                : 'Attach docket / invoice'}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ------------------------------------------------------ post */}
            <div className="sticky bottom-0 z-20 -mx-2 border-t border-border bg-background/95 px-2 py-3 backdrop-blur md:static md:mx-0 md:rounded-xl md:border md:px-4 md:py-4">
                {(!receipt || !hasDocket || !hasLines) && (
                    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 flex gap-2">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <p className="text-xs leading-relaxed">{blockedText}</p>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                        {Math.max(savedLines, pendingLines)} line
                        {Math.max(savedLines, pendingLines) === 1 ? '' : 's'} · {photos.length} photo
                        {photos.length === 1 ? '' : 's'} · docket {receipt?.docketNumber || 'not saved'}
                    </p>
                    <Button
                        className="h-12 w-full sm:w-auto"
                        disabled={!canPost || busy !== null}
                        onClick={post}
                    >
                        {busy === 'post' && <Loader2 size={16} className="animate-spin mr-2" />}
                        Post receipt
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                    Posting applies the quantities to the PO. It only closes the order if every
                    line is fully received — otherwise the PO stays in Receiving.
                </p>
            </div>

            <ImageLightbox
                alt="Receipt photo"
                images={lightboxImages}
                index={photoIndex === null
                    ? null
                    : lightboxImages.findIndex((image) => image.id === photoIndex)}
                onIndexChange={(index) => setPhotoIndex(index === null ? null : Number(lightboxImages[index]?.id))}
            />
        </div>
    );
}
